import type { Block, BlockItem, ChainLine, ChainPart, ExLine, Exercise, Id, Line, Load, Maxes, SetGroup, WorkoutContent } from './types';
import { blockTypeLabel, findFmt, plural3, stripFmt } from './format';

/* Работа с содержимым тренировки: проверки «пусто ли», подписи, расчёт весов. */

export type ExLookup = (id: Id) => Exercise | undefined;

let seq = 0;
/** Локальный id элемента внутри документа тренировки. */
export const nid = (p = 'i') =>
  p + Date.now().toString(36) + (++seq).toString(36) + Math.random().toString(36).slice(2, 6);

export const mkLine = (exId: Id | null, raw: string | null = null): ExLine => ({ t: 'ex', id: nid(), exId, raw });
export const mkChain = (parts: ChainPart[] = []): ChainLine => ({ t: 'chain', id: nid(), parts, sets: null, load: null, of: null });
export const mkSet = (items: Line[] = [], rounds = 3, rest = ''): SetGroup => ({ t: 'set', id: nid(), rounds, rest, items });
export const mkBlock = (p: Partial<Block> = {}): Block => ({ id: nid('b'), kind: null, title: '', note: '', fmt: null, items: [], ...p });
export const textBlock = (text = '', title = ''): Block => mkBlock({ text, title });
export const emptyWorkout = (): WorkoutContent => ({ title: '', blocks: [] });

export const isTextBlock = (b: Block) => typeof b.text === 'string';

/** «Отдых» — упражнение общей базы с нагрузкой во времени: строка отдыха между упражнениями
    (для таймера блока — отрезок отдыха, а не работы). Отдых между кругами сета — SetGroup.rest. */
export const REST_ID = 'own_rest';
export const isRest = (y: Line | BlockItem | null | undefined) => !!y && y.t === 'ex' && y.exId === REST_ID;
export const textLines = (t: string | null | undefined) =>
  String(t || '')
    .split('\n')
    .map((s) => s.trim())
    .filter(Boolean);
/** Пункт маркированного списка в блоке текстом */
export const LIST_RE = /^[•\-–—*][ \t]+/;

export const partHas = (p: ChainPart) => !!(p.exId || String(p.raw || '').trim());
export const lineHas = (y: Line): boolean => (y.t === 'chain' ? y.parts.some(partHas) : !!(y.exId || String(y.raw || '').trim()));
export const itemHas = (y: BlockItem): boolean => (y.t === 'set' ? y.items.some(lineHas) : lineHas(y));
export const blockHas = (b: Block) => (isTextBlock(b) ? textLines(b.text).length > 0 : b.items.some(itemHas));
/** Есть ли в дне тренировка. День из одного текста — тоже тренировка (CON-5). */
export const dayHas = (x: WorkoutContent | null | undefined) => !!x && x.blocks.some(blockHas);

/** Все строки блока, включая участников сетов */
export const flatLines = (b: Block): Line[] => b.items.flatMap((y) => (y.t === 'set' ? y.items : [y]));
/** Упражнений в блоке — без строк «Отдых» */
export const blockCount = (b: Block) => (isTextBlock(b) ? textLines(b.text).length : flatLines(b).filter((y) => lineHas(y) && !isRest(y)).length);
export const dayCount = (x: WorkoutContent) => x.blocks.reduce((a, b) => a + blockCount(b), 0);

/** «Где используется» для страницы упражнения (EX-1): стоит ли оно в блоке базы. */
export const blockUsesEx = (b: Block, exId: Id) =>
  !isTextBlock(b) && flatLines(b).some((y) => (y.t === 'chain' ? y.parts.some((p) => p.exId === exId) : y.exId === exId));

const firstTextLine = (t: string | null | undefined) => (textLines(t)[0] || '').replace(LIST_RE, '').replace(/:$/, '');
export const blockName = (b: Block) => b.title || blockTypeLabel(b) || (isTextBlock(b) ? firstTextLine(b.text) : '');

/** Формат из названия переезжает в fmt, блок становится комплексом (CON-18). */
export function normFmt(b: Block): Block {
  const f = findFmt(b.title);
  if (f) {
    const { src: _src, ...fmt } = f;
    if (!b.fmt) b.fmt = fmt;
    b.title = stripFmt(b.title);
  }
  if (b.fmt) b.kind = 'complex';
  return b;
}

/** Сет без двух участников распускается (CON-21). */
export function normSets(b: Block): Block {
  b.items = b.items.flatMap((y) => (y.t === 'set' && y.items.length < 2 ? y.items : [y]));
  return b;
}

/* ─── Нагрузка и рабочие веса (CON-16, CON-23, CON-24) ─── */

export const fmtNum = (v: number | string) => String(v).replace('.', ',');
const RNG = '–';

export const loadText = (l: Load | null | undefined) => {
  if (!l) return '';
  const r = fmtNum(l.from) + (l.to != null ? RNG + fmtNum(l.to) : '');
  return l.kind === 'pct' ? r + ' %' : r + ' ' + l.unit;
};

/** Ключ 1ПМ упражнения: общий ключ вариаций или само упражнение, если оно с весом. */
export const pmKey = (e: Exercise | undefined | null) => (e ? e.pm || (e.units.includes('кг') ? e.id : null) : null);

/** Часть связки, от 1ПМ которой считается %: выбранная тренером, иначе самая слабая с известным 1ПМ. */
export function chainOf(c: ChainLine, pm: Maxes | null, ex: ExLookup): ChainPart | null {
  const ws = c.parts.filter((p) => p.exId && pmKey(ex(p.exId)));
  if (!ws.length) return null;
  const pick = c.of && ws.find((p) => p.exId === c.of);
  if (pick) return pick;
  const max = (p: ChainPart) => (pm ? pm[pmKey(ex(p.exId!))!] : undefined);
  const known = ws.filter((p) => max(p));
  return known.length ? known.reduce((a, p) => (max(p)! < max(a)! ? p : a)) : ws[0];
}

/** Ключ 1ПМ строки или связки */
export function lineKey(it: Line, pm: Maxes | null, ex: ExLookup): string | null {
  if (it.t === 'chain') {
    const p = chainOf(it, pm, ex);
    return p ? pmKey(ex(p.exId!)) : null;
  }
  return it.exId ? pmKey(ex(it.exId)) : null;
}

export const roundTo = (v: number, step: number) => Math.round(v / step) * step;

/** Рабочий вес от % (нижняя и верхняя граница) или null, если посчитать нельзя. */
export function workKg(it: Line, pm: Maxes | null, ex: ExLookup): { lo: number; hi?: number } | null {
  if (!it.load || it.load.kind !== 'pct' || !pm) return null;
  const k = lineKey(it, pm, ex);
  if (!k) return null;
  const max = pm[k];
  if (!max) return null;
  const src = it.t === 'chain' ? chainOf(it, pm, ex) : it;
  const step = (src?.exId && ex(src.exId)?.step) || 2.5;
  const lo = roundTo((max * it.load.from) / 100, step);
  const hi = it.load.to != null ? roundTo((max * it.load.to) / 100, step) : undefined;
  return hi != null && hi !== lo ? { lo, hi } : { lo };
}

export const kgText = (w: { lo: number; hi?: number } | null) => (w ? fmtNum(w.lo) + (w.hi != null ? RNG + fmtNum(w.hi) : '') : null);

/** «3×(1+1+1)»: подходы связки и повторы частей */
export function chainScheme(c: ChainLine): string {
  const r = c.parts.filter(partHas).map((p) => p.reps || '1');
  if (!r.length) return '';
  const n = c.sets || 0;
  const k = c.per || 0;
  const body = r.length > 1 ? r.join('+') : r[0];
  const one = r.length > 1 ? '(' + body + ')' : body;
  /* подходы × связок в подходе × (повторы частей): 3×2×(1+1+1) */
  if (k > 1) return (n > 1 ? n + '×' : '') + k + '×' + one;
  return n > 1 ? n + '×' + one : body;
}

/** Блок упражнений → текст блока (меню блока «Разгруппировать в текстовый блок», как blockToText в прототипе):
    строка на упражнение — название, подходы, нагрузка, комментарий; связка — «A + B: 80 % — 3×(1+1)»;
    сет — заголовком «Суперсет 3 круга, отдых 1:00:» и упражнения под ним с «• ». Строку текстом — с её параметрами. */
export function blockToText(b: Block, ex: ExLookup): string {
  const line = (y: Line) => {
    if (y.t === 'chain') {
      const prm = [loadText(y.load), chainScheme(y)].filter(Boolean).join(' — ');
      return y.parts.filter(partHas).map((p) => partName(p, ex)).join(' + ') + (prm ? ': ' + prm : '');
    }
    return [y.exId ? ex(y.exId)?.ru || '' : String(y.raw || '').trim(), y.scheme, loadText(y.load), y.txt].filter(Boolean).join(' ');
  };
  return b.items
    .flatMap((y) =>
      y.t === 'set'
        ? y.items.some(lineHas)
          ? ['Суперсет ' + y.rounds + ' ' + plural3(+y.rounds, 'круг', 'круга', 'кругов') + (y.rest ? ', отдых ' + y.rest : '') + ':', ...y.items.filter(lineHas).map((l) => '• ' + line(l))]
          : []
        : lineHas(y)
          ? [line(y)]
          : [],
    )
    .join('\n');
}

export const partName = (p: ChainPart, ex: ExLookup) => (p.exId ? ex(p.exId)?.ru || '' : String(p.raw || '').trim());
export const lineName = (y: Line, ex: ExLookup) =>
  y.t === 'chain'
    ? y.parts
        .filter(partHas)
        .map((p) => partName(p, ex))
        .join(' + ')
    : y.exId
      ? ex(y.exId)?.ru || ''
      : String(y.raw || '');

/** Строка так, как её видит клиент: «3×5 Становая тяга 132,5 кг»; связка — «A + B: 90 кг — 3×(1+1+1)». Проценты клиенту не показываем. */
export function clientLine(y: Line, pm: Maxes | null, ex: ExLookup): string {
  const w = kgText(workKg(y, pm, ex));
  const load = w ? w + ' кг' : y.load && y.load.kind === 'abs' ? loadText(y.load) : '';
  if (y.t === 'chain') {
    const prm = [load, chainScheme(y)].filter(Boolean).join(' — ');
    return lineName(y, ex) + (prm ? ': ' + prm : '');
  }
  /* Упражнение не из базы: название текстом, но повторы и нагрузка — отдельно, как у упражнения из базы */
  if (y.txt) return lineName(y, ex) + ' ' + y.txt;
  if (!y.exId && !y.scheme && !y.load) return String(y.raw || '');
  return [y.scheme, lineName(y, ex), load].filter(Boolean).join(' ');
}

/** Клон содержимого с новыми id — для копирования дней, блоков, раздачи группе. */
export function cloneContent<T>(x: T): T {
  const c = JSON.parse(JSON.stringify(x));
  const walk = (o: any) => {
    if (Array.isArray(o)) o.forEach(walk);
    else if (o && typeof o === 'object') {
      if (typeof o.id === 'string') o.id = nid(o.id[0] || 'i');
      /* video — ссылка на загруженный файл (VideoRef), её id не трогаем */
      for (const [k, v] of Object.entries(o)) if (k !== 'video') walk(v);
    }
  };
  walk(c);
  return c;
}
